import { EqDialog, EqLinearProgress, theme } from "@equitable/equx";
import { ThemeProvider } from "@mui/material/styles";
import { appConfig } from "@/common/config/appConfig";
import { isUserAuthenticated } from "@/common/utils/helpers";
import { t } from "i18next";
import { useIdleTimer } from "react-idle-timer";
import { useEffect, useState } from "react";

const IdleTimeoutDialog = () => {
  const REMAINING_COUNTER = +(appConfig?.AUTH0_WARNING_TIMEOUT_IN_MINS) * 60; // 120 Seconds
  const [, setState] = useState("Active");
  const [counter, setCounter] = useState<number>(REMAINING_COUNTER);
  const IDLE_TIME = +(appConfig?.AUTH0_EXPIRE_TIMEOUT_MINS) * 60 * 1000;
  const [remainingTime, setRemainingTime] = useState<number>(
    Math.ceil(IDLE_TIME / 1000)
  );
  const [open, setOpen] = useState(false);

  const resetDefaultValuesOnAction = () => {
    setCounter(REMAINING_COUNTER);
    setRemainingTime(Math.ceil(IDLE_TIME / 1000));
  };

  const onIdle = () => {
    setState("Idle");
  };

  const onActive = () => {
    setState("Active");
  };

  const onAction = () => {
    if (!open) {
      resetDefaultValuesOnAction();
    }
  };

  const { getRemainingTime, start } = useIdleTimer({
    startManually: true,
    crossTab: false,
    onIdle,
    onActive,
    onAction,
    timeout: IDLE_TIME,
    throttle: 500,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      checkAuthentication();
      if (open === false) {
        setRemainingTime(Math.ceil(getRemainingTime() / 1000));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  });

  useEffect(() => {
    const counterTimer = setInterval(() => {
      if (remainingTime === 0) {
        setOpen(true);
        setCounter((prevCounter) => {
          if (prevCounter === 0) {
            onIdleState();
            clearInterval(counterTimer);

            return 0;
          }

          return prevCounter - 1;
        });
      }
    }, 1000);

    return () => {
      clearInterval(counterTimer);
    };
  }, [remainingTime]);

  const checkAuthentication = () => {
    if (isUserAuthenticated() && getRemainingTime() === IDLE_TIME) {
      start();
    }
  };

  const handleAction = () => {
    resetDefaultValuesOnAction();
    setOpen(false);
  };

  const onIdleState = () => {
    resetDefaultValuesOnAction();
    setOpen(false);
    window.location.href = appConfig.EQUINET_SIGNOUT_URL;
  };

  return (
    <ThemeProvider theme={theme}>
      <EqDialog
        open={open}
        actionButtons={[
          {
            onClick: handleAction,
            color: "surfaceBrandDark",
            label: t("continue"),
          },
        ]}
        onClose={handleAction}
        rightButtonIcon="X"
        title={t("sessionExpiryWarning")}
      >
        {t("sessionExpiryMessage", { counter: counter })}
        <EqLinearProgress
          linearSx={{
            margin: "20px 0px 0px 0px",
          }}
          value={
            100 - ((REMAINING_COUNTER - counter) / REMAINING_COUNTER) * 100
          }
          variant="determinate"
        />
      </EqDialog>
    </ThemeProvider>
  );
};

export default IdleTimeoutDialog;